import { useCallback, useRef } from "react";
import type { KeycloakProfile } from "keycloak-js"; // retained for userProfile param (kept for API compatibility)
import type { IProperty } from "../../property-provider";
import { KeycloakSingleton } from "../../keycloak-provider";
import { assistantAuthHeaders } from "../assistantAuthHeaders";

export interface AssistantHeadersParams {
  property: IProperty;
  userProfile: KeycloakProfile | undefined;
  contextEnabled: boolean;
  section: string;
  shortLocale: string;
  conversationId: string;
}

/**
 * Returns a stable `buildHeaders` callback that reads live values at call time
 * via a ref — never captures stale closures, never forces transport re-creation.
 *
 * The callback signature matches DefaultChatTransport's `headers` option.
 */
export function useAssistantHeaders(params: AssistantHeadersParams): () => Record<string, string> {
  const ref = useRef(params);
  ref.current = params;

  return useCallback(() => {
    const { property, contextEnabled, section, shortLocale, conversationId } = ref.current;
    return {
      "Content-Type": "application/json",
      "Accept-Language": shortLocale,
      // Read straight off the Keycloak singleton on every call: keycloak-js rotates the access
      // token in place (autoRefreshToken), so anything captured earlier could already be stale.
      ...assistantAuthHeaders(property.token, KeycloakSingleton?.token),
      "x-context": contextEnabled ? section : "none",
      "x-property": property.ticker,
      "x-chat-session-id": conversationId,
    };
  }, []);
}
